export type PurchaseOrderReferenceFormatItemKind =
  "digit" | "letter" | "uppercase" | "lowercase" | "symbol" | "fixed" | "exact"

export interface PurchaseOrderReferenceFormatItem {
  kind: PurchaseOrderReferenceFormatItemKind
  value?: string
}

export interface PurchaseOrderReferenceFormatPosition {
  placeholder: string
  fixed?: boolean
  matches: (character: string) => boolean
}

const STRUCTURED_FORMAT_PREFIX = "p1:"

export function normalizePurchaseOrderReferenceFormat(
  value: string | null | undefined
): string | null {
  return value?.trim() || null
}

export function parsePurchaseOrderReferenceFormat(
  value: string | null | undefined
): PurchaseOrderReferenceFormatItem[] {
  const normalizedFormat = normalizePurchaseOrderReferenceFormat(value)
  if (!normalizedFormat) return []

  if (!normalizedFormat.startsWith(STRUCTURED_FORMAT_PREFIX)) {
    return [...normalizedFormat].map((character) =>
      /[0-9]/.test(character)
        ? { kind: "digit" }
        : {
            kind: "fixed",
            value: character
          }
    )
  }

  const encodedItems = normalizedFormat.slice(STRUCTURED_FORMAT_PREFIX.length)
  if (!encodedItems) return []

  try {
    return encodedItems.split(",").map((encodedItem) => {
      if (encodedItem === "d") return { kind: "digit" }
      if (encodedItem === "l") return { kind: "letter" }
      if (encodedItem === "lu") return { kind: "uppercase" }
      if (encodedItem === "ll") return { kind: "lowercase" }
      if (encodedItem === "s") return { kind: "symbol" }
      if (encodedItem.startsWith("f:") || encodedItem.startsWith("e:")) {
        return {
          kind: encodedItem.startsWith("e:") ? "exact" : "fixed",
          value: decodeURIComponent(encodedItem.slice(2))
        }
      }
      throw new Error("Onbekend formatonderdeel")
    })
  } catch {
    return []
  }
}

export function serializePurchaseOrderReferenceFormat(
  items: PurchaseOrderReferenceFormatItem[]
): string | null {
  if (!items.length) return null

  const encodedItems = items.map((item) => {
    if (item.kind === "digit") return "d"
    if (item.kind === "letter") return "l"
    if (item.kind === "uppercase") return "lu"
    if (item.kind === "lowercase") return "ll"
    if (item.kind === "symbol") return "s"
    return `${item.kind === "exact" ? "e" : "f"}:${encodeURIComponent(item.value || "")}`
  })

  return `${STRUCTURED_FORMAT_PREFIX}${encodedItems.join(",")}`
}

export function purchaseOrderReferenceFormatExample(
  format: string | null | undefined
): string | null {
  const normalizedFormat = normalizePurchaseOrderReferenceFormat(format)
  if (!normalizedFormat) return null

  const positions = purchaseOrderReferenceFormatPositions(normalizedFormat)
  if (!positions.length) return null

  return positions.map((position) => position.placeholder).join("")
}

export function purchaseOrderReferenceFormatPositions(
  format: string | null | undefined
): PurchaseOrderReferenceFormatPosition[] {
  const normalizedFormat = normalizePurchaseOrderReferenceFormat(format)
  if (!normalizedFormat) return []
  if (!normalizedFormat.startsWith(STRUCTURED_FORMAT_PREFIX)) {
    return [...normalizedFormat].map((character) => ({
      placeholder: character,
      fixed: !/[0-9]/.test(character),
      matches: /[0-9]/.test(character)
        ? (actualCharacter: string) => /[0-9]/.test(actualCharacter)
        : (actualCharacter: string) =>
            actualCharacter.toLocaleLowerCase("nl") === character.toLocaleLowerCase("nl")
    }))
  }

  const items = parsePurchaseOrderReferenceFormat(normalizedFormat)

  return items.flatMap((item) => {
    if (item.kind === "digit") {
      return [{ placeholder: "0", matches: (character: string) => /[0-9]/.test(character) }]
    }
    if (item.kind === "letter") {
      return [{ placeholder: "A", matches: (character: string) => /[A-Za-z]/.test(character) }]
    }
    if (item.kind === "uppercase") {
      return [{ placeholder: "A", matches: (character: string) => /^[A-Z]$/.test(character) }]
    }
    if (item.kind === "lowercase") {
      return [{ placeholder: "a", matches: (character: string) => /^[a-z]$/.test(character) }]
    }
    if (item.kind === "symbol") {
      return [{ placeholder: "-", matches: (character: string) => /[^A-Za-z0-9]/.test(character) }]
    }
    return [...(item.value || "")].map((fixedCharacter) => ({
      placeholder: fixedCharacter,
      fixed: true,
      matches: (character: string) =>
        item.kind === "exact"
          ? character === fixedCharacter
          : character.toLocaleLowerCase("nl") === fixedCharacter.toLocaleLowerCase("nl")
    }))
  })
}

export function purchaseOrderReferenceCharacterMatches(
  reference: string | null | undefined,
  format: string | null | undefined
): boolean[] {
  const positions = purchaseOrderReferenceFormatPositions(format)
  return [...(reference || "")].map(
    (character, index) => positions[index]?.matches(character) ?? false
  )
}

export function purchaseOrderReferenceMatchesFormat(
  reference: string | null | undefined,
  format: string | null | undefined
): boolean {
  const normalizedFormat = normalizePurchaseOrderReferenceFormat(format)
  const normalizedReference = reference?.trim() || ""
  if (!normalizedFormat || !normalizedReference) return true

  const positions = purchaseOrderReferenceFormatPositions(normalizedFormat)
  const actualCharacters = [...normalizedReference]
  return (
    positions.length === actualCharacters.length &&
    purchaseOrderReferenceCharacterMatches(normalizedReference, normalizedFormat).every(Boolean)
  )
}

export function purchaseOrderReferenceFormatError(format: string | null | undefined): string {
  return `Gebruik het verwachte format, bijvoorbeeld "${purchaseOrderReferenceFormatExample(format)}".`
}
